import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setCustomerReference1Error from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference1ErrorHandler";
import setcustomerReference1 from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference1Handler";

import { TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => {
    const cartState = getCurrentCartState(state).value;

    return {
        cartState,
        currentBillTo: getBillToState(state, cartState?.billToId).value,
        customerReference1Error: state.KGPState.CheckoutReview.customerReference1Error,
        customerReference1: state.KGPState.CheckoutReview.customerReference1,
        isAwaitingApproval: cartState?.isAwaitingApproval,
    };
};

const mapDispatchToProps = {
    setcustomerReference1,
    setCustomerReference1Error,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    customerReferenceGrid?: GridItemProps;
    customerReferenceChildGrid?: GridItemProps;
    customerReferenceSelect?: SelectPresentationProps;
    customerOrderReferenceLabelStyle?: InjectableCss;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    customerReferenceGrid: {
        width: 12,
        css: css`
            padding: 0px 5px 3px 16px;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    customerReferenceChildGrid: {
        width: 12,
        css: css`
            padding: 0px 5px 20px 16px;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    customerOrderReferenceLabelStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: 600;
        `,
    },
};

export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const CustomerReference1Field: FC<Props> = ({
    setcustomerReference1,
    currentBillTo,
    customerReference1,
    customerReference1Error,
    setCustomerReference1Error,
    isAwaitingApproval,
    cartState,
}) => {
    const [properties, setProperties] = useState(currentBillTo?.properties);
    const [customerRefChild, setCustomerRefChild] = useState(customerReference1 || "");
    const [customerReference1Val, setCustomerReference1Val] = useState(customerReference1 || "");
    const [customerRefError, setCustomerRefError] = useState(
        properties?.customerOrderReferenceRequired1?.toLowerCase() === "true" ? customerReference1Error : "",
    );

    useEffect(() => {
        properties?.customerOrderReferenceRequired1?.toLowerCase() === "true" &&
            setCustomerRefError(customerReference1Error);
    }, [customerReference1Error]);

    useEffect(() => {
        setProperties(currentBillTo?.properties);
    }, [currentBillTo?.properties]);

    useEffect(() => {
        setcustomerReference1({ customerReference1: customerReference1Val || "" });
    }, [customerReference1Val]);

    useEffect(() => {
        setcustomerReference1({ customerReference1: customerRefChild || "" });
    }, [customerRefChild]);

    useEffect(() => {
        if (
            cartState?.properties &&
            cartState.properties?.customerOrderRefSelectedValue1 &&
            cartState.properties?.customerOrderRefSelectedValue1.split("|").length > 1
        ) {
            setCustomerReference1Val(cartState.properties.customerOrderRefSelectedValue1.split("|")[1]);
            setCustomerRefChild(cartState.properties.customerOrderRefSelectedValue1.split("|")[1]);
        }
    }, [cartState?.properties]);

    const customerReferenceDropDownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const error =
            e.target.value.trim() === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel1 ?? "")} is required`
                : "";

        setCustomerRefChild(e.target.value);
        setcustomerReference1({ customerReference1: e.target.value });
        if (properties?.customerOrderReferenceRequired1?.toLowerCase() === "true") {
            setCustomerReference1Error({ customerReference1Error: error });
        }
    };

    const customerReferenceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const error =
            e.target.value.trim() === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel1 ?? "")} is required`
                : "";

        setCustomerReference1Val(e.target.value);
        setcustomerReference1({ customerReference1: e.target.value });
        if (properties?.customerOrderReferenceRequired1?.toLowerCase() === "true") {
            setCustomerReference1Error({ customerReference1Error: error });
        }
    };

    if (!properties?.customerOrderReferenceType1 || properties?.customerOrderReferenceAttribute1 === "None") {
        return null;
    }

    return (
        <GridContainer>
            <GridItem {...styles.customerReferenceChildGrid}>
                {properties?.customerOrderReferenceType1 === "Dropdown" ? (
                    <Select
                        {...styles.customerReferenceSelect}
                        label={properties.customerOrderReferenceLabel1}
                        value={customerRefChild}
                        onChange={customerReferenceDropDownChange}
                        ref={null}
                        required={properties?.customerOrderReferenceRequired1?.toLowerCase() === "true"}
                        error={
                            properties?.customerOrderReferenceRequired1?.toLowerCase() === "true" && customerRefError
                        }
                        disabled={isAwaitingApproval}
                    >
                        <option value="">{translate("Select option")}</option>
                        {properties?.listReferencesValues1.split("|").map(method => (
                            <option key={method} value={method}>
                                {method}
                            </option>
                        ))}
                    </Select>
                ) : (
                    <TextField
                        label={properties?.customerOrderReferenceLabel1}
                        value={customerReference1Val}
                        onChange={customerReferenceInputChange}
                        required={properties?.customerOrderReferenceRequired1?.toLowerCase() === "true"}
                        error={
                            properties?.customerOrderReferenceRequired1?.toLowerCase() === "true" && customerRefError
                        }
                        disabled={isAwaitingApproval}
                    />
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerReference1Field),
    definition: {
        displayName: "Customer Reference 1 Field",
        group: "Checkout - Review & Submit",
        allowedContexts: [CheckoutReviewAndSubmitPageContext],
    },
};

export default widgetModule;
